import DeleteIcon from '@mui/icons-material/Delete';
import { useCallback, useEffect, useState } from 'react';
import PureModal from 'react-pure-modal';
import { uuidv4 } from '../../../../../internal/utils/functions';
import { setImageGalleryWidgetConfig } from '../../../../../state/dashboard/slice';
import { ModalCloseButton } from '../../../commons/ModalCloseButton';

import { ApiReturnResource } from '@highimpact-frontend/api-types';
import {
  Box,
  Button,
  Divider,
  IconButton,
  ImageList,
  ImageListItem,
  Skeleton,
  Typography,
} from '@mui/material';
import { useDashboardHost } from '../../../../../context/DashboardHostContext';
import {
  useDashboardLibraryDispatch,
  useDashboardLibrarySelector,
} from '../../../../../runtime/DashboardLibraryProvider';
import { updateAndPersistImageGalleryTags } from '../../../../../state/asyncThunks';
import { ApiReturnImageResource } from './ImageGalleryDisplayer';

interface ImageGalleryConfigContentProps {
  site_id: number;
  project_id: number;
  grid_type: 'reports' | 'grids';
  grid_id: number;
  widget_id: string;
  ref: React.LegacyRef<HTMLDivElement>;
}

const ImageGalleryConfigContent: React.FC<ImageGalleryConfigContentProps> = ({
  site_id,
  project_id,
  grid_type,
  grid_id,
  widget_id,
  ref,
}) => {
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<
    ApiReturnImageResource[]
  >([]);
  const [deletedExistingImgIds, setDeletedExistingImgIds] = useState<number[]>(
    []
  );
  const [existingLoadingImagesStatu, setExistingLoadingImagesStatu] = useState<
    'idle' | 'loading' | 'data' | 'error'
  >('idle');

  const dispatch = useDashboardLibraryDispatch();
  const host = useDashboardHost();

  const tags = useDashboardLibrarySelector(
    (state) =>
      (state.dashboard[grid_type][grid_id].widgets[widget_id] as any)?.props
        .tags
  );

  const closeModal = useCallback(() => {
    dispatch(setImageGalleryWidgetConfig({ state: 'closed' }));
  }, [dispatch]);

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const files = Array.from(event.dataTransfer.files).filter((file) =>
      file.type.startsWith('image/')
    );
    setPendingFiles((prev) => [...prev, ...files]);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []).filter((file) =>
      file.type.startsWith('image/')
    );
    setPendingFiles((prev) => [...prev, ...files]);
  };

  const handleDeletePending = (index: number): void => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExistingImage = (imgId: number) => {
    setExistingImages((prev) => prev.filter((im) => im.id !== imgId));
    setDeletedExistingImgIds((prev) => [...prev, imgId]);
  };

  const handleSave = async () => {
    const widgetUid = `${widget_id}-${uuidv4()}`;
    const finalTags = tags && tags.length > 0 ? tags : [widgetUid];

    try {
      if (!tags || tags.length === 0) {
        await host.api.tag.add({ name: widgetUid, is_generic: false });
        await host.api.tag.associateTagToProject([widgetUid], project_id);
        await dispatch(
          updateAndPersistImageGalleryTags({
            gridId: grid_id,
            gridType: grid_type,
            widgetId: widget_id,
            tags: [widgetUid],
          })
        );
      }

      if (deletedExistingImgIds.length > 0) {
        await Promise.all(
          deletedExistingImgIds.map(async (imgId) => {
            await host.api.tag.deleteFromResource(imgId, finalTags);
          })
        );
        setDeletedExistingImgIds([]);
      }

      if (pendingFiles.length > 0) {
        await Promise.all(
          pendingFiles.map(async (file) => {
            const response = await host.api.resource.add({
              project_id,
              tag_names: finalTags,
              name: file.name,
              resource_type: 'blob',
              type: 'image',
              file,
            });
            await host.api.tag.associateTagToResource(response.id, finalTags);
          })
        );
        setPendingFiles([]);
      }

      closeModal();
    } catch (error) {
      console.error('Erreur pendant la sauvegarde :', error);
    }
  };

  useEffect(() => {
    let unmounted = false;
    setExistingLoadingImagesStatu('loading');
    host.api.resource
      .getByEntity({ project_id, site_id: null, device_id: null })
      .then((response) => {
        if (unmounted) return;
        const resources = response as ApiReturnResource[];
        const blobs = resources.filter(
          (rsc) =>
            rsc.resource_type === 'blob' &&
            rsc.tag_names?.some((tag) => tags?.includes(tag))
        );
        setExistingLoadingImagesStatu('data');
        setExistingImages(blobs as ApiReturnImageResource[]);
      });
    return () => {
      unmounted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project_id, tags]);

  return (
    <PureModal
      width="60vh"
      header="Configuration"
      isOpen
      closeButton={<ModalCloseButton />}
      closeButtonPosition="header"
      onClose={closeModal}
    >
      <div ref={ref}>
        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          Fichiers en attente d'import
        </Typography>
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          sx={{
            border: '2px dashed #ccc',
            borderRadius: '8px',
            padding: '20px',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: '#fafafa',
            mb: 2,
          }}
        >
          <Typography variant="body1" sx={{ mb: 2 }}>
            Glisser-déposer
          </Typography>
          <Button variant="contained" component="label" sx={{ mb: 2 }}>
            Sélectionner des fichiers
            <input
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handleFileInput}
            />
          </Button>
          {pendingFiles.length > 0 && (
            <ImageList
              cols={3}
              rowHeight={160}
              sx={{ mt: 2 }}
              variant="masonry"
            >
              {pendingFiles.map((file, index) => (
                <ImageListItem
                  key={file.name + index}
                  sx={{ position: 'relative' }}
                >
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    style={{
                      objectFit: 'cover',
                      width: '100%',
                      height: '100%',
                    }}
                  />
                  <IconButton
                    aria-label="delete"
                    onClick={() => handleDeletePending(index)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 1,
                    }}
                  >
                    <DeleteIcon color="error" />
                  </IconButton>
                </ImageListItem>
              ))}
            </ImageList>
          )}
        </Box>

        {/* Existing images */}
        <Divider sx={{ my: 3 }}>Images déjà existantes</Divider>
        {existingLoadingImagesStatu === 'loading' ? (
          <ImageList cols={3} rowHeight={160} sx={{ mt: 2 }}>
            {Array.from({ length: 6 }).map((_, idx) => (
              <ImageListItem key={idx}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={160}
                  sx={{ borderRadius: 2 }}
                />
              </ImageListItem>
            ))}
          </ImageList>
        ) : existingLoadingImagesStatu === 'data' &&
          existingImages.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Aucune image existante trouvée.
          </Typography>
        ) : (
          <ImageList cols={3} rowHeight={160} sx={{ mt: 2 }}>
            {existingImages.map((img) => (
              <ImageListItem key={img.id} sx={{ position: 'relative' }}>
                <img
                  src={`data:image/png;base64,${img.value}`}
                  alt={img.name}
                  style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                />
                <IconButton
                  aria-label="delete"
                  onClick={() => handleDeleteExistingImage(img.id)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 1,
                  }}
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </ImageListItem>
            ))}
          </ImageList>
        )}
        <div style={{ display: 'flex', gap: '15px', marginTop: '15px' }}>
          <Button
            variant="contained"
            fullWidth
            sx={{ mb: 3 }}
            onClick={handleSave}
          >
            Ajouter
          </Button>
        </div>
      </div>
    </PureModal>
  );
};

export default ImageGalleryConfigContent;
